"use client";

import { useRef } from "react";
import Image, { getImageProps } from "next/image";
import { useHeroParallax } from "./useHeroParallax";

type HeroBackgroundProps = {
    mobileSrc: string;
    desktopSrc: string;
};

function HeroBackgroundImages({
    mobileSrc,
    desktopSrc,
}: HeroBackgroundProps) {
    const {
        props: desktopImageProps,
    } = getImageProps({
        src: desktopSrc,
        alt: "",
        fill: true,
        quality: 95,
        sizes: "110vw",
        fetchPriority: "high",
    });

    const {
        props: mobileImageProps,
    } = getImageProps({
        src: mobileSrc,
        alt: "Dr. Sarah Daccarett, MD, founder and CEO of Inner Balance",
        fill: true,
        quality: 75,
        sizes: "100vw",
        loading: "eager",
        fetchPriority: "high",
    });

    return (
        <>
            <link
                rel="preload"
                as="image"
                imageSrcSet={desktopImageProps.srcSet}
                imageSizes="110vw"
                media="(min-width: 768px)"
                fetchPriority="high"
            />
            <link
                rel="preload"
                as="image"
                imageSrcSet={mobileImageProps.srcSet}
                imageSizes="100vw"
                media="(max-width: 767px)"
                fetchPriority="high"
            />
            <picture>
                <source
                    media="(min-width: 768px)"
                    srcSet={desktopImageProps.srcSet}
                    sizes={desktopImageProps.sizes}
                />
                <Image
                    src={mobileSrc}
                    alt="Dr. Sarah Daccarett, MD, founder and CEO of Inner Balance"
                    fill
                    fetchPriority="high"
                    quality={75}
                    sizes="100vw"
                    loading="eager"
                    className="object-cover object-center"
                />
            </picture>
        </>
    );
}

export function HeroBackground({ mobileSrc, desktopSrc }: HeroBackgroundProps) {
    const clipRef = useRef<HTMLDivElement>(null);
    const layerRef = useRef<HTMLDivElement>(null);
    const mediaRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef<HTMLDivElement>(null);

    useHeroParallax({
        clipRef,
        layerRef,
        mediaRef,
        zoomRef,
    });

    return (
        <div
            ref={clipRef}
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
        >
            <div
                className="absolute inset-0 md:inset-x-0 md:top-0 md:h-[calc(100%+100svh)] motion-reduce:md:h-auto"
            >
                <div className="h-full w-full md:sticky md:top-0 md:h-[calc(100%-100svh)] md:overflow-hidden motion-reduce:md:static motion-reduce:md:h-full">
                    <div
                        ref={layerRef}
                        className="absolute inset-0 md:will-change-transform"
                    >
                        <div
                            ref={mediaRef}
                            className="absolute inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:min-h-full md:min-w-full md:-translate-x-1/2 md:-translate-y-1/2 motion-reduce:md:inset-0 motion-reduce:md:min-h-0 motion-reduce:md:min-w-0 motion-reduce:md:translate-x-0 motion-reduce:md:translate-y-0"
                        >
                            <div
                                ref={zoomRef}
                                className="relative h-full w-full lg:scale-[1.06] lg:will-change-transform motion-reduce:lg:scale-100"
                            >
                                <HeroBackgroundImages
                                    mobileSrc={mobileSrc}
                                    desktopSrc={desktopSrc}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
